import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ConfigFieldsComponent } from '../../../../_directives/config-fields.component';
import { AlertService, RegionService, UserService } from '../../../../_services';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  ConfigFieldType,
  CRMField,
  CRMFieldSet,
  FormatRule,
  getFormatRuleMessage,
  ListOptionRule,
  ProcessorAddressField,
  ProcessorConfigRuleSet,
  ProcessorField,
  Region,
} from '../../../../_models';
import { CustomValidators } from '../../../../_helpers';
import { FormError } from '../../../../_forms/form';
import { takeUntil } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  moduleId: module.id.toString(),
  selector: 'processor-config-fields',
  templateUrl: './processor-config-fields.component.html',
  styleUrls: ['./processor-config-fields.component.scss'],
})
export class ProcessorConfigFieldsComponent extends ConfigFieldsComponent implements OnInit, OnChanges {
  // Format rules for the selected processor, keyed by fieldset and field name
  @Input('rules') rules: ProcessorConfigRuleSet = {};
  fieldTypes = ConfigFieldType;
  addressField = ProcessorAddressField;
  countries: Region[] = [];
  statesByFieldset: { [fieldset: string]: Region[] } = {};
  protected countrySubscriptions: Subscription[] = [];

  constructor(
    protected router: Router,
    protected location: Location,
    protected alertService: AlertService,
    protected userService: UserService,
    protected regionService: RegionService
  ) {
    super(router, location, alertService, userService);
  }

  ngOnInit() {
    this.regionService
      .getCountries()
      .pipe(takeUntil(this._destroy$))
      .subscribe((countries: Region[]) => {
        this.countries = countries;
      });

    super.ngOnInit();
  }

  getStates(fieldset: string): Region[] {
    return this.statesByFieldset[fieldset] || [];
  }

  isStateSelect(fieldset: string, field: ProcessorField): boolean {
    return field.name === ProcessorAddressField.State && this.getStates(fieldset).length > 0;
  }

  protected watchCountry(fieldset: string, fieldGroup: FormGroup) {
    const country = fieldGroup.get(ProcessorAddressField.Country);

    this.loadStates(fieldset, country.value);
    const subscription = country.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((value: string) => {
      const state = fieldGroup.get(ProcessorAddressField.State);

      if (state) {
        state.setValue(null);
      }

      this.loadStates(fieldset, value);
    });

    this.countrySubscriptions.push(subscription);
  }

  protected loadStates(fieldset: string, country: string) {
    this.regionService
      .list(country)
      .pipe(takeUntil(this._destroy$))
      .subscribe((states: Region[]) => {
        this.statesByFieldset[fieldset] = Array.isArray(states) ? states : [];
      });
  }

  ngOnChanges() {
    this.updateForm();
  }

  public showFieldErrors(name: string, fieldset: string, label: string): FormError[] {
    const group = this.form.get(fieldset) as FormGroup;
    const control = group.get(name) as FormControl;
    let errors: FormError[];

    if (control && (control.touched || this.submitted) && control.errors) {
      const rule = this.getRule(fieldset, name);
      const formatError = control.errors.pattern || control.errors.minlength || control.errors.maxlength;

      // A server message (the 'string' error) and "required" read fine as they are; a broken format is spelled out
      errors = this.getErrors(control);
      if (rule && formatError && !control.errors.required) {
        errors = [{ error: 'string', params: { string: getFormatRuleMessage(label, rule) } }];
      } else if (control.errors.oneOf) {
        errors = [{ error: 'string', params: { string: getFormatRuleMessage(label, ListOptionRule) } }];
      }
    }

    return errors;
  }

  // Processor numbers are codes such as 000123, which a number input would turn into 123
  getFieldType(field: CRMField) {
    let type = super.getFieldType(field);

    if (type === ConfigFieldType.Number) {
      type = ConfigFieldType.Text;
    }

    return type;
  }

  protected getRule(fieldset: string, name: string): FormatRule {
    let rule: FormatRule = null;

    if (this.rules && this.rules[fieldset] && this.rules[fieldset][name]) {
      rule = this.rules[fieldset][name];
    }

    return rule;
  }

  protected updateForm() {
    this.countrySubscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.countrySubscriptions = [];

    if (this.formGroup) {
      // Clear existing controls inside this formGroup (processor_config)
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.removeControl(key);
      });

      const groups: CRMFieldSet[] = [];

      if (this.inputFields && this.inputFields.length > 0) {
        this.inputFields.forEach((group: any) => {
          if (group.fields || group.fields.length !== 0) {
            const fieldGroup = new FormGroup({});

            group.fields.forEach((field: ProcessorField) => {
              let defaultValue = null;
              const validators = [];

              // Resolve default value from passed "values"
              if (this.values && this.values[group.fieldset] && this.values[group.fieldset][field.name] !== undefined) {
                defaultValue = this.values[group.fieldset][field.name];
              } else if (field.default !== undefined) {
                defaultValue = field.default;
              }

              if (field.name === ProcessorAddressField.Country && defaultValue) {
                defaultValue = String(defaultValue).toLowerCase();
              }

              if (field.required) {
                validators.push(Validators.required);
              }

              if (field.min !== undefined) {
                validators.push(Validators.min(field.min));
              }

              if (field.max !== undefined) {
                validators.push(Validators.max(field.max));
              }

              // A saved value the list no longer offers would show an empty select yet pass as filled in
              if (field.type === ConfigFieldType.Select && field.options) {
                validators.push(CustomValidators.oneOf(field.options.map((option) => option.value)));
              }

              const rule = this.getRule(group.fieldset, field.name);
              if (rule) {
                validators.push(...CustomValidators.formatRule(rule));
              }

              // Add FormControl to the group
              fieldGroup.addControl(field.name, new FormControl(defaultValue, validators));

              // Initialize password toggle
              field.showPassword = false;
            });

            // ✅ Add this fieldset group to the local formGroup (processor_config)
            this.formGroup.addControl(group.fieldset, fieldGroup);

            if (fieldGroup.get(ProcessorAddressField.Country)) {
              this.watchCountry(group.fieldset, fieldGroup);
            }

            groups.push(group);
          }
        });
      }

      // Save for template usage
      this.fields = groups;
      this.form = this.formGroup;
    }
  }
}
